import {
  getEntityRelations,
  humanizeEntityRef,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { startCase } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { CatalogGridItem } from './types';
import { Content, ContentHeader, Progress } from '@backstage/core-components';
import {
  FormControl,
  Grid,
  Input,
  InputLabel,
  TablePagination,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import {
  DEFAULT_NAMESPACE,
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { CardTitleOptions } from './CardTitle';
import { CatalogGridAction } from './GidActions/GridActions';
import { CatalogError } from './CatalogError';
import { useDefaultCatalogTableActions } from './defaultActions';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavigateFunction } from 'react-router/dist/lib/hooks';
import { AnalyticsURLParamUtils } from '../../../utils/analytics';
import {
  useMayContributeEntities,
  useMayReuseEntities,
} from '@internal/backstage-plugin-catalog-additional-actions';
import { DefaultCardItem, UserCardItem } from './CardItem';
import { useGetMultipleEntities } from './useGetEntities';
import { useCatalogCustomOrder } from '../../../hooks/useCatalogCustomOrder';
import useAsync from 'react-use/lib/useAsync';
import { useAnalytics } from '@backstage/core-plugin-api';

export const entityTransformer = (
  entity: Entity,
): { entity: Entity; resolved: any } => {
  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  return {
    entity,
    resolved: {
      name: humanizeEntityRef(entity, {
        defaultKind: 'Component',
      }),
      ownedByRelationsTitle: ownedByRelations
        .map(r => humanizeEntityRef(r, { defaultKind: 'group' }))
        .join(', '),
      ownedByRelations,
      partOfSystemRelationTitle: partOfSystemRelations
        .map(r =>
          humanizeEntityRef(r, {
            defaultKind: 'system',
          }),
        )
        .join(', '),
      partOfSystemRelations,
    },
  };
};

export interface CatalogGridProps<T extends CatalogGridItem> {
  actions?: CatalogGridAction<T>[];
  options?: CardTitleOptions;
}

export function compileEntitiesNamespacesToCounter(entities: Entity[]): {
  [key: string]: number;
} {
  return entities.reduce((result: { [key: string]: number }, entity) => {
    const namespace: string | undefined = entity.metadata?.namespace;
    if (!namespace) {
      return result;
    }
    if (!result[namespace]) {
      result[namespace] = 0;
    }
    result[namespace] += 1;
    return result;
  }, {} as { [key: string]: number });
}

export function entitiesCountersToString(entitiesCounters: {
  [key: string]: number;
}): string {
  return Object.entries(entitiesCounters)
    .filter(([key]) => key !== 'default')
    .map(([key, value]) => {
      return `${startCase(key)} instances (${value})`;
    })
    .join(', ');
}

export function updateQueryParams(
  navigate: NavigateFunction,
  newPage?: number,
  newRowsPerPage?: number,
) {
  const searchParams = new URLSearchParams(location.search);
  if (newPage !== undefined) {
    searchParams.set('page', newPage.toString());
  }
  if (newRowsPerPage !== undefined) {
    searchParams.set('rowsPerPage', newRowsPerPage.toString());
  }
  navigate(`${location.pathname}?${searchParams.toString()}`, {
    replace: true,
  });
}

export function getAllowedOrDefault<T>(
  defaultValue: T,
  value?: T,
  allowed?: T[],
): T {
  return (value !== undefined &&
    allowed !== undefined &&
    allowed.indexOf(value) === -1) ||
    value === undefined
    ? defaultValue
    : value;
}

/** @public */
export function CatalogGrid<T extends CatalogGridItem>(
  props: CatalogGridProps<T>,
) {
  const { actions } = props;
  const analytics = useAnalytics();

  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const { isMayContributeEntity, toggleMayContributeEntity } =
    useMayContributeEntities();
  const { isMayReuseEntity, toggleMayReuseEntity } = useMayReuseEntities();
  const { loading, error, entities, filters, queryParameters } =
    useEntityList();
  const { componentsWithCounters } = useGetMultipleEntities(
    [...entities],
    'direct',
  );

  const { defaultActions } = useDefaultCatalogTableActions<CatalogGridItem>({
    isStarredEntity,
    toggleStarredEntity,
    isMayContributeEntity,
    toggleMayContributeEntity,
    isMayReuseEntity,
    toggleMayReuseEntity,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(window.location.search);
  const catalogCustomOrder = useCatalogCustomOrder();
  const catalogCustomOrderData = useAsync(async () => {
    return catalogCustomOrder();
  }, []);
  const catalogCustomSort = (catalogCustomOrderData?.value ?? []).reduce(
    (acc: { [ref: string]: number }, item: { ref: string; sort: number }) => {
      acc[item.ref] = item.sort;
      return acc;
    },
    {} as { [ref: string]: number },
  );

  AnalyticsURLParamUtils.saveQueryParamsToAnalytics(queryParams);
  const [page, setPage] = useState(
    parseInt(queryParams.get('page') || '0', 10) || 0,
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    getAllowedOrDefault(
      10,
      parseInt(queryParams.get('rowsPerPage') || '10', 10),
      [10, 25, 50, 100],
    ),
  );
  const [textFilter, setTextFilter] = useState(
    queryParams.get('textFilter') || '',
  );
  const updateTextFilter = (updatedTextFilter: string) => {
    setTextFilter(updatedTextFilter);
    queryParams.set('textFilter', updatedTextFilter);
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true,
    });
  };
  const realTitle =
    filters.user?.value && filters.user?.value === 'starred'
      ? 'Interested'
      : filters.user?.value;
  const titlePreamble = startCase(realTitle ?? 'all');
  const queryParamTags = useMemo(
    () => [queryParameters.tags].flat().filter(Boolean) as string[],
    [queryParameters.tags],
  );

  // Listen for page changes and update the page state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newPage = parseInt(params.get('page') || '0', 10);

    if (newPage !== page) {
      setPage(newPage);
    }
  }, [location.search, page]);

  useEffect(() => {
    AnalyticsURLParamUtils.setAnalytics(analytics);
  }, [analytics]);

  if (error) {
    return <CatalogError error={error} />;
  }
  if (loading) {
    return <Progress />;
  }
  const textFilteredEntities = entities
    .filter(entity => {
      const emptyFilter = !textFilter || textFilter === '';
      const nameCondition = entity?.metadata?.name
        ?.toLocaleLowerCase()
        .includes(textFilter?.toLocaleLowerCase());
      const descriptionCondition = entity?.metadata?.description
        ?.toLocaleLowerCase()
        .includes(textFilter?.toLocaleLowerCase());
      return emptyFilter || nameCondition || descriptionCondition;
    })
    .sort((a, b) => {
      const name = a.metadata.name.localeCompare(b.metadata.name);
      let lifecycle = 0;
      if (a.spec?.lifecycle === b.spec?.lifecycle) {
        lifecycle = 0;
      } else if (a.spec?.lifecycle === 'coming-soon') {
        lifecycle = -1;
      } else if (b.spec?.lifecycle === 'coming-soon') {
        lifecycle = 1;
      }
      const refA: string = stringifyEntityRef({
        kind: a.kind,
        name: a.metadata.name,
        namespace: a.metadata.namespace || DEFAULT_NAMESPACE,
      });
      const refB: string = stringifyEntityRef({
        kind: b.kind,
        name: b.metadata.name,
        namespace: b.metadata.namespace || DEFAULT_NAMESPACE,
      });
      const orderA = catalogCustomSort[refA] || 0;
      const orderB = catalogCustomSort[refB] || 0;
      // eslint-disable-next-line no-nested-ternary
      const customOrder = orderA === orderB ? 0 : orderA < orderB ? 1 : -1;

      return customOrder || lifecycle || name;
    });

  const activeEntitiesCounters: { [key: string]: number } =
    compileEntitiesNamespacesToCounter(textFilteredEntities);
  const activeEntitiesCountersString: string = entitiesCountersToString(
    activeEntitiesCounters,
  );

  const showPagination =
    props.options?.alwaysShowPagination ||
    textFilteredEntities.length > rowsPerPage;

  const rows = textFilteredEntities
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map(entityTransformer);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    updateQueryParams(navigate, newPage);
  };

  const handleChangeRowsPerPage = (event: { target: { value: string } }) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    updateQueryParams(navigate, 0, newRowsPerPage);
  };

  if (rows.length === 0 && page !== 0) {
    setPage(0);
  }
  return (
    <Content className="grid-view">
      <ContentHeader
        title={`${titlePreamble} (${activeEntitiesCounters.default || 0})`}
        description={`${activeEntitiesCountersString}`}
      >
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item>
            <FilterListIcon />
          </Grid>
          <Grid item>
            <FormControl>
              <InputLabel htmlFor="search-filter">Filter</InputLabel>
              <Input
                id="search-filter"
                type="search"
                value={textFilter}
                onChange={e => updateTextFilter(e.target.value)}
              />
            </FormControl>
          </Grid>
        </Grid>
      </ContentHeader>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
      >
        {rows.map((row, i) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={row.entity.metadata.uid ?? `entity-${i}`}
          >
            {row.entity.kind === 'User' ? (
              <UserCardItem
                row={row}
                options={props.options}
                actions={actions}
                queryParamTags={queryParamTags}
                defaultActions={defaultActions}
                componentsWithCounters={componentsWithCounters}
              />
            ) : (
              <DefaultCardItem
                row={row}
                options={props.options}
                actions={actions}
                queryParamTags={queryParamTags}
                defaultActions={defaultActions}
              />
            )}
          </Grid>
        ))}
      </Grid>
      {showPagination && (
        <TablePagination
          component="div"
          count={textFilteredEntities.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Content>
  );
}
